import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Dialog from '../../../../../dialog/Dialog';
import usePeriodicalReport from '../../../../../../hooks/periodical-report/usePeriodicalReport';
import { useParams } from 'react-router-dom';
import { validate } from '../../../../../../utils';
import isEmpty from 'lodash/isEmpty';
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import useFetchData from '../../../../../../hooks/useFetchData';
import FormBasicInformation from './FormBasicInformation';
import AlertSaveButton from '../../../../../button/AlertSaveButton';
import IconTooltipButton from '../../../../../button/IconTooltipButton';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import FormSubject from './FormSubject';
import FormPreview from './FormPreview';
import { getCurriculumByIdDetail } from '../../../../../../services/curriculum/curriculum.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import usePeriodicalReporMarking from '../../../../../../hooks/periodical-report/usePeriodicalReporMarking';

const CreateEditAssessmentArea = ({ show, onClose, type, formData = {}, codes = [] }) => {
  const steps = ['Basic Information', 'Subject Selected', 'Preview'];

  const { periodicalReportId } = useParams();
  const {
    isLoading: isLoadingAssessmentAreas,
    data: assessments,
    error: errorAssessmentAreas
  } = useFetchData('/periodical-report/setting/assessment-area');

  const types = ['delete', 'edit', 'edit-basic-information', 'edit-subject', 'add', 'generate-table', 'generate-table-by', 'sync-marking-by'];
  const curriculum = useSelector((state) => state?.assessment?.curriculum || [])
  const [loading, toggleLoading] = useState(false);
  const [isLoadingC, toggleLoadingC] = useState(false);
  const [currentCurriculum, setCurrentCurriculum] = useState(null);
  const [errors, setInputErrors] = useState({});
  const [form, setInputForm] = useState(formData);
  const [step, setStep] = useState(0);

  const {
    addAssessmentAreaToPeriodicalReport,
    updateAssessmentAreaToPeriodicalReport,
    removeAssessmentAreaFromPeriodicalReport,

    generatePeriodicalReportMarking,
    generatePeriodicalReportMarkingByAssessmentArea,
  } = usePeriodicalReport();

  const {
    syncMarkingDataPeriodicalReportMarkingByIdAndAssessmentArea
  } = usePeriodicalReporMarking();

  const onRedirect = (step) => {
    setStep(step);
  };
  const onNext = () => {
    setInputErrors(null);
    if (step === 0) {
      const rules = {
        codeSelected: 'required',
        yearLevelId: 'required'
      };
      const errors = validate(rules, form);
      if (!isEmpty(errors)) {
        setInputErrors(errors);
        return;
      } else {
        onRedirect(parseInt(step) + 1);
      }
    } else {
      onRedirect(parseInt(step) + 1);
    }
  };

  const onChange = (name, value) => {
    if (name === 'curriculumId') {
      setInputForm({
        curriculumId: value,
        yearLevel: form.yearLevel
      });
    } else if (name === 'codeSelected') {
      const code = assessments?.find((item) => item.code === value.code && item.curriculumId === value.curriculumId);
      setInputForm({
        ...form,
        [name]: value,
        curriculumId: code?.curriculumId,
        assessmentData: code,
        subject: code?.subject || []
      });
      setInputErrors({ ...errors, [name]: null });
    } else {
      setInputForm({ ...form, [name]: value });
      setInputErrors({ ...errors, [name]: null });
    }
  };

  const onConfirm = useCallback(
    async (e) => {
      if (!types.includes(type)) {
        setInputErrors({ message: 'Invalid type' });
        return;
      }
      e.preventDefault();
      toggleLoading(true);
      try {
        let result = { error: false };
        if (type === 'delete') {
          result = await removeAssessmentAreaFromPeriodicalReport(periodicalReportId, form._id);
        }
        if (type === 'generate-table') {
          result = await generatePeriodicalReportMarking(periodicalReportId);
        }
        if (type === 'generate-table-by') {
          result = await generatePeriodicalReportMarkingByAssessmentArea(periodicalReportId, form._id);
        }
        if (type === 'sync-marking-by') {
          result = await syncMarkingDataPeriodicalReportMarkingByIdAndAssessmentArea(form._id);
        }
        if (type === 'add') {
          result = await addAssessmentAreaToPeriodicalReport(periodicalReportId, form);
        }
        if (type.includes('edit')) {
          result = await updateAssessmentAreaToPeriodicalReport(periodicalReportId, form._id, form);
        }

        if (result.error) {
          setInputErrors(result);
        } else {
          onClose();
        }
      } finally {
        toggleLoading(false);
      }
    },
    [form, type, periodicalReportId]
  );

  const optionsAssessmentAreas = useMemo(() => {
    return (
      assessments
        ?.filter((item) => {
          const find = codes?.find((value) => item.code === value.code && item.curriculumId === value.curriculumId)
          return !find
        })
        ?.map((item) => ({
          value: item.code,
          label: `${item.code} - ${item.name}`,
          curriculum: curriculum?.find((curr) => curr._id === item.curriculumId)?.name || '',
          ...item
        }))
        ?.sort((a, b) => {
          if (a.curriculum < b.curriculum) return -1;
          if (a.curriculum > b.curriculum) return 1;
          if (a.value < b.value) return -1;
          if (a.value > b.value) return 1;
          return 0;
        }) || []
    );
  }, [assessments, codes]);


  
  useEffect(() => {
    const fetchData = async () => {
      if (type === 'generate-table') {
        if (form.assessmentAreas?.length === 0) {
          setInputErrors({ message: 'Please select at least one assessment area' });
          return;
        }
      }
      if (!form?.curriculumId) return;
      toggleLoadingC(true);
      try {
        const responseC = await getCurriculumByIdDetail(form?.curriculumId);
        if (responseC?.data) {
          setCurrentCurriculum(responseC.data);
        }
      } finally {
        toggleLoadingC(false);
      }
    };
    fetchData();
  }, [form?.curriculumId, type]);

  const Actions = () => (
    <>
      <ButtonStartIcon
        disabled={loading}
        onClick={onClose}
        startIcon={['fal', 'times']}
        color='primary'
        variant='outlined'
      >
        {type === 'delete' ? 'Cancel' : 'Close'}
      </ButtonStartIcon>
      <ButtonStartIcon
        disabled={loading || errors?.message}
        loading={loading}
        onClick={onConfirm}
        startIcon={['fal', 'check']}
        color='primary'
      >
        {['generate-table', 'delete', 'generate-table-by', 'sync-marking-by'].includes(type)  ? 'Confirm' : 'Save'}
      </ButtonStartIcon>
    </>
  );

  return (
    <Dialog
      id={`periodical-report-assessment-areas-${type}-${periodicalReportId}`}
      show={show}
      onClose={onClose}
      maxWidth={[ 'delete', 'preview', 'generate-table', 'generate-table-by', 'sync-marking-by'].includes(type) ? 'xs' : ['edit-basic-information'].includes(type) ? 'sm' : 'md'}
      title={`${type?.replace(/-/g, ' ')} assessment area`}
      onCloseHeader={type === 'preview' ? true : false}
      actions={['add', 'preview'].includes(type) ? undefined : Actions()}
    >
      {loading && 
        <div className='absolute inset-0 bg-loading z-10 flex items-center justify-center'>
          <FontAwesomeIcon  icon='circle-notch' spin size='2x' className='text-muted-foreground opacity-50' />
        </div>
      }
      {['add'].includes(type) && (
        <div className='h-screen-3/4 '>
          <Box className='px-1 py-2 gap-4 h-10 border-b border-b-2 border-primary text-primary-foreground flex items-center justify-between'>
            <Stepper color='primary' activeStep={parseInt(step)} className='w-full'>
              {steps.map((step, index) => (
                <Step key={`step-${index}`}>
                  <StepLabel>{step}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <div className='flex items-center justify-end gap-4'>
              <div className='min-w-25px'>
                {parseInt(step) > 0 && (
                  <IconTooltipButton
                    disabled={loading}
                    title='Previous'
                    icon='chevron-left'
                    classNameIcon='text-lg'
                    style={{
                      borderRadius: '0.5rem',
                      padding: '0.45rem',
                      color: 'var(--primary)',
                      backgroundColor: '#fff',
                      width: 25,
                      height: 25
                    }}
                    className='shadow-sm'
                    onClick={() => onRedirect(parseInt(step) - 1)}
                  />
                )}
              </div>
              <div className='min-w-100px'>
                {parseInt(step) === steps.length - 1 ? (
                  <ButtonStartIcon
                    disabled={loading}
                    loading={loading}
                    onClick={onConfirm}
                    startIcon={['fal', 'check']}
                    color='primary'
                    className='w-full'
                  >
                    Save
                  </ButtonStartIcon>
                ) : (
                  <IconTooltipButton
                    disabled={loading}
                    title='Next'
                    icon='chevron-right'
                    classNameIcon='text-lg'
                    style={{
                      borderRadius: '0.5rem',
                      padding: '0.45rem',
                      color: 'var(--primary)',
                      backgroundColor: '#fff',
                      width: 25,
                      height: 25
                    }}
                    className='shadow-sm'
                    onClick={onNext}
                  />
                )}
              </div>
              <AlertSaveButton
                submodule='Assessment areas'
                disabled={loading}
                icon={['fal', 'times']}
                title='Cancel'
                color='primary'
                variant='outlined'
                className='shadow-sm'
                onDontSave={onClose}
              />
            </div>
          </Box>
          <Box className='absolute top-20 bottom-4 inset-x-4 '>
            {step === 0 && (
              <FormBasicInformation
                form={form}
                errors={errors}
                onChange={onChange}
                type={type}
                optionsAssessmentAreas={optionsAssessmentAreas}
                isLoadingAssessmentAreas={isLoadingAssessmentAreas}
                errorAssessmentAreas={errorAssessmentAreas}
                currentCurriculum={currentCurriculum}
                curriculum={curriculum}
              />
            )}
            {step === 1 && (
              <FormSubject
                form={form}
                errors={errors}
                onChange={onChange}
                type={type}
                isLoading={isLoadingC || loading}
                currentCurriculum={currentCurriculum}
              />
            )}
            {step === 2 && (
              <FormPreview
                form={form}
                errors={errors}
                onChange={onChange}
                optionsAssessmentAreas={optionsAssessmentAreas}
                isLoadingAssessmentAreas={isLoadingAssessmentAreas}
                errorAssessmentAreas={errorAssessmentAreas}
                currentCurriculum={currentCurriculum}
              />
            )}
          </Box>
        </div>
      )}
      {['edit-basic-information'].includes(type) && (
        <FormBasicInformation
          form={form}
          errors={errors}
          onChange={onChange}
          type={type}
          optionsAssessmentAreas={optionsAssessmentAreas}
          isLoadingAssessmentAreas={isLoadingAssessmentAreas}
          errorAssessmentAreas={errorAssessmentAreas}
          currentCurriculum={currentCurriculum}
          isLoading={loading}
          curriculum={curriculum}

        />
      )}
      {['edit-subject'].includes(type) && (
        <div className='h-screen-3/4 relative'>
          <FormSubject
            form={form}
            errors={errors}
            onChange={onChange}
            type={type}
            isLoading={isLoadingC}
            loading={loading}
            currentCurriculum={currentCurriculum}
          />
        </div>
      )}
      {['preview'].includes(type) && (
        <FormPreview
          form={form}
          errors={errors}
          onChange={onChange}
          optionsAssessmentAreas={optionsAssessmentAreas}
          isLoadingAssessmentAreas={isLoadingAssessmentAreas}
          errorAssessmentAreas={errorAssessmentAreas}
          currentCurriculum={currentCurriculum}
        />
      )}
      {['delete'].includes(type) && (
        <div>
          <Typography component={'span'}>Are you sure you want to {type} the assessment area? </Typography>
          <b>{form.code}</b>
        </div>
      )}
      {['generate-table'].includes(type) && (
        <div>
          <Typography component={'span'}>Are you sure you want to generate the table for all assessment areas? </Typography>
        </div>
      )}
      {['generate-table-by'].includes(type) && (
        <div>
          <Typography component={'span'}>Are you sure you want to generate the table for the assessment area ? </Typography>
          <b>{form.code}</b>
        </div>
      )}
      {['sync-marking-by'].includes(type) && (
        <div>
          <Typography component={'span'}>Are you sure you want to sync marking data by assessment area? </Typography>
          <b>{form.code}</b>
        </div>
      )}
      {errors?.message && (
        <Alert severity='error' className='mt-4'>
          {errors?.message}
        </Alert>
      )}
    </Dialog>
  );
};

export default CreateEditAssessmentArea;
